/**
 * @class VideoPlayer
 * @extends HTMLElement
 *
 * A custom web component for rendering video content using the Plyr library.
 * Supports HTML5 video as well as embedded YouTube and Vimeo sources.
 *
 * Plyr.js is used to provide a consistent, accessible player UI across all sources.
 */
export class VideoPlayer extends HTMLElement {
  #plyr = null;

  get host() {
    return this.dataset.host || 'html';
  }

  connectedCallback() {
    this.#initPlayer();
  }

  disconnectedCallback() {
    this.#destroyPlayer();
  }

  #initPlayer() {
    const targetElement = this.host === 'html'
      ? this.querySelector('video')
      : this.querySelector('[data-ref="plyrEmbed"]');

    if (!targetElement) return;

    const autoplayIntent = this.#resolveAutoplayIntent(targetElement);
    const hasControls = this.#resolveControlsIntent(targetElement);
    const options = this.#buildPlyrOptions(targetElement);

    this.#plyr = new Plyr(targetElement, {
      ...options,
      autoplay: false,
      controls: hasControls ? options.controls : [],
    });

    this.#plyr.on('ready', () => this.#applyAutoplayIntent(autoplayIntent));
  }

  #destroyPlayer() {
    this.#plyr?.destroy();
    this.#plyr = null;
  }

  #buildPlyrOptions(targetElement) {
    const trackElement = targetElement.querySelector('track[kind="captions"]');
    const hasCaptions = trackElement !== null;

    const merchantConfig = this.#parseConfigAttribute();

    const baseControls = [
      'play',
      'progress',
      'current-time',
      'mute',
      ...(hasCaptions ?
          ['captions'] : []
      ),
    ];

    const captionsConfig = hasCaptions
      ? {
        captions: {
          active: true,
          language: trackElement.srclang || 'auto',
          update: true
        }
      }
      : {};

    return {
      playsinline: true,
      hideControls: false,
      controls: baseControls,
      ...captionsConfig,
      ...merchantConfig,
      youtube: {
        ...merchantConfig.youtube,
        noCookie: true
      },
      vimeo: {
        ...merchantConfig.vimeo,
        dnt: 1
      },
      storage: {
        enabled: false
      }
    };
  }

  /**
   * Optionally accept a full Plyr config object via `data-plyr-config='{ "title": "Example Title" }'`
   * for advanced overrides without changing the JS source.
   */
  #parseConfigAttribute() {
    const rawConfig = this.dataset.plyrConfig;

    if (!rawConfig) return {};

    try {
      return JSON.parse(rawConfig);
    } catch {
      return {};
    }
  }

  /**
   * Resolves autoplay and mute intent from the target element before Plyr
   * takes ownership. For native video, attributes are stripped so Plyr does
   * not lock its internal mute state at boot, making the mute toggle remain
   * fully interactive after autoplay starts.
   *
   * @param {HTMLVideoElement|HTMLDivElement} targetElement
   * @returns {{ shouldAutoplay: boolean, shouldMute: boolean }}
   */
  #resolveAutoplayIntent(targetElement) {
    if (this.host === 'html') {
      const shouldAutoplay = targetElement.hasAttribute('autoplay');
      const shouldMute = targetElement.hasAttribute('muted');

      targetElement.removeAttribute('autoplay');
      targetElement.removeAttribute('muted');

      return { shouldAutoplay, shouldMute };
    }

    const iframeSrc = targetElement.querySelector('iframe')?.src ?? '';

    return {
      shouldAutoplay: iframeSrc.includes('autoplay=1'),
      shouldMute: iframeSrc.includes('mute=1') || iframeSrc.includes('background=1'),
    };
  }

  /**
   * Applies resolved autoplay intent after Plyr is ready. Mute state is
   * set via the Plyr API rather than HTML attributes so the mute toggle
   * remains interactive.
   *
   * @param {{ shouldAutoplay: boolean, shouldMute: boolean }} intent
   */
  #applyAutoplayIntent({ shouldAutoplay, shouldMute }) {
    if (shouldMute) {
      this.#plyr.muted = true;
      this.#plyr.volume = 0;
    }

    if (shouldAutoplay) {
      this.#plyr.play();
    }
  }

  #resolveControlsIntent(targetElement) {
    if (this.host === 'html') {
      const hasControls = targetElement.hasAttribute('controls');
      targetElement.removeAttribute('controls');
      return hasControls;
    }

    const iframeSrc = targetElement.querySelector('iframe')?.src ?? '';
    return iframeSrc.includes('controls=1');
  }

  play() {
    this.#plyr?.play();
  }

  pause() {
    this.#plyr?.pause();
  }

  get paused() {
    return this.#plyr?.paused ?? true;
  }
}

if (!customElements.get('video-player')) {
  customElements.define('video-player', VideoPlayer);
}
